<template>
  <section class="pitch-booking">
    <MainNavbar />

    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <h1 class="header-title">Find Your Perfect Sport Facility</h1>
        <p class="header-subtitle">Discover and book sports facilities near you</p>
      </div>
    </div>

    <!-- Fixed Filters Section -->
    <div class="filters-section">
      <div class="filters-container">
        <!-- Main Search -->
        <div class="main-search">
          <i class="fas fa-search search-icon"></i>
          <input 
            v-model="filters.search" 
            type="text" 
            placeholder="Search by sport facility name..." 
            @input="debouncedFilterPitches"
            class="search-input"
          />
          <button v-if="filters.search" @click="removeFilter('search')" class="clear-search-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Filter Grid -->
        <div class="filter-grid">
          <!-- Sport facility type filter -->
          <div class="filter-group">
            <label>
              <i class="fas fa-running"></i>
              Sport Type
            </label>
            <select v-model="filters.pitch_type" @change="filterPitches" class="filter-select">
              <option value="">All Sports</option>
              <option
                v-for="opt in facilityTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <!-- County Filter -->
          <div class="filter-group">
            <label>
              <i class="fas fa-map-marker-alt"></i>
              County
            </label>
            <select v-model="filters.county" @change="updateZoneOptions" class="filter-select">
              <option value="">All Counties</option>
              <option value="Nairobi">Nairobi</option>
              <option value="Kiambu">Kiambu</option>
              <option value="Mombasa">Mombasa</option>
              <option value="Kisumu">Kisumu</option>
            </select>
          </div>

          <!-- Zone Filter (dynamic) -->
          <div class="filter-group">
            <label>
              <i class="fas fa-map-pin"></i>
              Zone
            </label>
            <div class="select-wrapper">
              <select v-model="filters.zone" @change="filterPitches" class="filter-select" :disabled="!filters.county">
                <option value="">All Zones</option>
                <option v-for="zone in filteredZones" :key="zone" :value="zone">{{ zone }}</option>
              </select>
              <i class="fas fa-chevron-down select-arrow"></i>
            </div>
          </div>

          <!-- Sport facility tier filter -->
          <div class="filter-group">
            <label>
              <i class="fas fa-layer-group"></i>
              Tier
            </label>
            <select v-model="filters.tier" @change="filterPitches" class="filter-select">
              <option value="">All Tiers</option>
              <option value="premium">Premium</option>
              <option value="standard">Standard</option>
              <option value="basic">Basic</option>
            </select>
          </div>

          <!-- Capacity Filter -->
          <div class="filter-group">
            <label>
              <i class="fas fa-users"></i>
              Capacity
            </label>
            <select v-model="filters.capacity" @change="filterPitches" class="filter-select">
              <option value="">Any Capacity</option>
              <option value="small">Small (1-20 people)</option>
              <option value="medium">Medium (21-50 people)</option>
              <option value="large">Large (51-99 people)</option>
              <option value="extra_large">Extra Large (100+ people)</option>
            </select>
          </div>

          <!-- Price Filter -->
          <div class="filter-group">
            <label>
              <i class="fas fa-tag"></i>
              Price Range
            </label>
            <select v-model="filters.price" @change="filterPitches" class="filter-select">
              <option value="">Any Price</option>
              <option value="budget">Budget (≤ Ksh 1,000)</option>
              <option value="midrange">Mid-range (Ksh 1,001-3,000)</option>
              <option value="premium">Premium (Ksh 3,001-6,000)</option>
              <option value="luxury">Luxury (Ksh 6,000+)</option>
            </select>
          </div>

          <!-- Sort Options -->
          <div class="filter-group">
            <label>
              <i class="fas fa-sort"></i>
              Sort By
            </label>
            <div class="select-wrapper">
              <select v-model="filters.sort_by" @change="filterPitches" class="filter-select">
                <option value="created_at">Newest First</option>
                <option value="hourly_rate">Price: Low to High</option>
                <option value="hourly_rate_desc">Price: High to Low</option>
                <option value="pitch_capacity">Capacity: Low to High</option>
                <option value="pitch_capacity_desc">Capacity: High to Low</option>
                <option value="pitch_name">Name: A-Z</option>
              </select>
              <i class="fas fa-chevron-down select-arrow"></i>
            </div>
          </div>

          <!-- Clear Filters Button -->
          <div class="filter-group">
            <label>&nbsp;</label>
            <button @click="clearFilters" class="clear-btn" :class="{ 'has-filters': hasActiveFilters }">
              <i class="fas fa-times"></i> 
              <span>Clear All</span>
              <span v-if="hasActiveFilters" class="filter-count">{{ activeFilterCount }}</span>
            </button>
          </div>
        </div>

        <!-- Active Filters Display -->
        <transition name="slide-down">
          <div v-if="hasActiveFilters" class="active-filters">
            <div class="active-filters-header">
              <span class="active-filters-label">
                <i class="fas fa-filter"></i>
                Active Filters
              </span>
              <button @click="clearFilters" class="clear-small-btn">
                <i class="fas fa-times"></i> Clear All
              </button>
            </div>
            <div class="active-filter-tags">
              <transition-group name="tag-fade">
                <span v-if="filters.search" key="search" class="filter-tag">
                  <i class="fas fa-search"></i>
                  "{{ filters.search }}"
                  <i class="fas fa-times remove-tag" @click="removeFilter('search')"></i>
                </span>
                <span v-if="filters.pitch_type" :key="filters.pitch_type" class="filter-tag">
                  <i class="fas fa-running"></i>
                  {{ formatFilterValue(filters.pitch_type) }}
                  <i class="fas fa-times remove-tag" @click="removeFilter('pitch_type')"></i>
                </span>
                <span v-if="filters.county" :key="filters.county" class="filter-tag">
                  <i class="fas fa-map-marker-alt"></i>
                  {{ filters.county }}
                  <i class="fas fa-times remove-tag" @click="removeFilter('county')"></i>
                </span>
                <span v-if="filters.zone" :key="filters.zone" class="filter-tag">
                  <i class="fas fa-map-pin"></i>
                  {{ filters.zone }}
                  <i class="fas fa-times remove-tag" @click="removeFilter('zone')"></i>
                </span>
                <span v-if="filters.tier" :key="filters.tier" class="filter-tag">
                  <i class="fas fa-layer-group"></i>
                  {{ formatFilterValue(filters.tier) }}
                  <i class="fas fa-times remove-tag" @click="removeFilter('tier')"></i>
                </span>
                <span v-if="filters.capacity" :key="filters.capacity" class="filter-tag">
                  <i class="fas fa-users"></i>
                  {{ getCapacityLabel(filters.capacity) }}
                  <i class="fas fa-times remove-tag" @click="removeFilter('capacity')"></i>
                </span>
                <span v-if="filters.price" :key="filters.price" class="filter-tag">
                  <i class="fas fa-tag"></i>
                  {{ getPriceLabel(filters.price) }}
                  <i class="fas fa-times remove-tag" @click="removeFilter('price')"></i>
                </span>
              </transition-group>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Results Summary -->
    <transition name="fade">
      <div v-if="!isLoading" class="results-summary">
        <div class="results-container">
          <p class="results-text">
            <span class="results-count">{{ filteredPitches.length }}</span>
            sport {{ filteredPitches.length === 1 ? 'facility' : 'facilities' }} found
            <span v-if="hasActiveFilters" class="results-filtered">(filtered)</span>
          </p>
        </div>
      </div>
    </transition>

    <!-- Sport facilities grid -->
    <transition-group name="staggered-fade" tag="div" class="pitch-grid">
      <div 
        v-for="(pitch, index) in filteredPitches" 
        :key="pitch.id"
        :data-index="index"
        class="pitch-card"
      >
        <!-- Sport facility image -->
        <div class="pitch-image">
          <img
            v-if="hasPitchImage(pitch)"
            :src="pitch.images[0]"
            :alt="pitch.pitch_name"
            loading="lazy"
            decoding="async"
            referrerpolicy="no-referrer"
            @error="onPitchImageError(pitch.id)"
          />
          <div v-else class="no-image">
            <i class="fas fa-image"></i>
          </div>
          <div class="image-overlay">
            <span class="price-tag">
              Ksh {{ formatPrice(pitch.hourly_rate) }}/hr
            </span>
            <span class="tier-tag" :class="pitch.pitch_tier">{{ pitch.pitch_tier }}</span>
          </div>
        </div>

        <div class="pitch-content">
          <div class="pitch-header">
            <h3>{{ pitch.pitch_name }}</h3>
          </div>

          <div class="pitch-location">
            <p><i class="fas fa-map-marker-alt"></i> {{ pitch.address }}</p>
            <p class="location-details">{{ pitch.zone }}, {{ pitch.resident_county }}</p>
          </div>

          <div class="pitch-features">
            <div class="feature">
              <i class="fas fa-running"></i>
              <span>{{ formatPitchType(pitch.pitch_type) }}</span>
            </div>
            <div class="feature">
              <i class="fas fa-users"></i>
              <span>{{ pitch.capacity }} people</span>
            </div>
          </div>

          <div class="pitch-actions">
            <button class="book-btn" @click.stop="bookPitch(pitch)">
              <i class="fas fa-calendar-check"></i>
              <span>Book Now</span>
            </button>
            <button class="details-btn" @click.stop="viewPitchDetails(pitch)">
              <i class="fas fa-info-circle"></i>
              <span>Details</span>
            </button>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <div v-if="filteredPitches.length === 0 && !isLoading" key="no-results" class="no-results">
        <div class="no-results-content">
          <div class="no-results-icon">
            <i class="fas fa-search"></i>
          </div>
          <h3>No sport facilities found</h3>
          <p>Try adjusting your search criteria or clear filters</p>
          <button @click="clearFilters" class="clear-all-btn">
            <i class="fas fa-times"></i>
            Clear All Filters
          </button>
        </div>
      </div>
    </transition-group>

    <!-- Back to Top Button -->
    <transition name="fade">
      <button v-if="showBackToTop" @click="scrollToTop" class="back-to-top">
        <i class="fas fa-chevron-up"></i>
      </button>
    </transition>
    <div class="page-image">
      <img
        src="../../assets/images/football_pitch/varietysports.jpg"
        alt="Variety of sports facilities available on IpromiseAfrica"
        loading="lazy"
        decoding="async"
      />
    </div>
    <div class="edit-pitch-footer-host">
      <ThirdpartyFooter />
    </div>
  </section>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { debounce } from 'lodash-es';
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import { buildApiUrl } from "@/config/api";
import {
  FACILITY_TYPE_OPTIONS,
  formatFacilityType,
} from "@/utils/facilityTypes";

export default {
  loadingMessage: 'Loading available sport facilities...',
  mixins: [globalLoadingMixin],
  name: "FacilityDetails",
  components: { MainNavbar, ThirdpartyFooter },
  data() {
    return {
      facilityTypeOptions: FACILITY_TYPE_OPTIONS,
      isLoading: false,
      pitches: [],
      filteredPitches: [],
      imageErrors: {},
      showBackToTop: false,
      allZones: {
        Nairobi: [
          "Westlands", "Dagoretti North", "Dagoretti South", "Langata", 
          "Kibra", "Roysambu", "Kasarani", "Ruaraka", "Embakasi South", 
          "Embakasi North", "Embakasi Central", "Embakasi East", 
          "Embakasi West", "Makadara", "Kamukunji", "Starehe"
        ],
        Mombasa: ["Changamwe", "Jomvu", "Kisauni", "Likoni", "Mvita", "Nyali"],
        Kisumu: [
          "Kisumu Central", "Kisumu East", "Kisumu West", "Seme", 
          "Nyando", "Muhoroni", "Nyakach"
        ],
        Kiambu: [
          "Gatundu North", "Gatundu South", "Githunguri", "Juja", 
          "Kabete", "Kiambaa", "Kiambu Town", "Kikuyu", "Lari", 
          "Limuru", "Ruiru", "Thika Town"
        ]
      },
      filters: {
        search: "",
        pitch_type: "",
        county: "",
        zone: "",
        tier: "",
        capacity: "",
        price: "",
        sort_by: "created_at"
      }
    };
  },
  computed: {
    filteredZones() {
      if (!this.filters.county || !this.allZones[this.filters.county]) {
        return [];
      }
      return this.allZones[this.filters.county];
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(value => 
        value && value !== "created_at"
      );
    },
    activeFilterCount() {
      return Object.values(this.filters).filter(value => 
        value && value !== "created_at"
      ).length;
    }
  },
  mounted() {
    const { type_filter, tier_filter, tier } = this.$route.query;
    if (type_filter) this.filters.pitch_type = String(type_filter);
    if (tier_filter || tier) this.filters.tier = String(tier_filter || tier);
    this.fetchAvailablePitches();
    this.debouncedFilterPitches = debounce(this.filterPitches, 300);
    this.setupScrollListener();
  },
  beforeUnmount() {
    this.removeScrollListener();
  },
  methods: {
    /** API may return stringified JSON for ``images``; normalize to URL strings for <img src>. */
    normalizePitchImageUrls(images) {
      if (images == null) return [];
      if (Array.isArray(images)) return images.filter(Boolean);
      if (typeof images === "string") {
        const s = images.trim();
        if (!s) return [];
        try {
          const parsed = JSON.parse(s);
          return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } catch {
          return [];
        }
      }
      return [];
    },
    mapPitchesWithNormalizedImages(pitches) {
      if (!Array.isArray(pitches)) return [];
      return pitches.map((p) => ({
        ...p,
        images: this.normalizePitchImageUrls(p.images),
      }));
    },
    async fetchAvailablePitches() {
      this.isLoading = true;
      try {
        const queryParams = new URLSearchParams();
        
        Object.entries(this.filters).forEach(([key, value]) => {
          if (value && key !== 'sort_by') {
            const paramName = key === 'search' ? 'search' : 
                            key === 'pitch_type' ? 'type_filter' :
                            key === 'tier' ? 'tier_filter' :
                            key === 'county' ? 'county_filter' :
                            key === 'zone' ? 'zone_filter' :
                            key === 'capacity' ? 'capacity_filter' :
                            key === 'price' ? 'price_filter' : key;
            
            queryParams.append(paramName, value);
          }
        });

        if (this.filters.sort_by) {
          if (this.filters.sort_by.includes('_desc')) {
            queryParams.append('sort_by', this.filters.sort_by.replace('_desc', ''));
            queryParams.append('sort_order', 'desc');
          } else {
            queryParams.append('sort_by', this.filters.sort_by);
            queryParams.append('sort_order', 'asc');
          }
        }

        const response = await fetch(buildApiUrl(`/api/facilities/all?${queryParams}`), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch sport facilities: ${response.status}`);
        }

        const result = await response.json();
        this.imageErrors = {};

        const facilityList = result.facilities || result.pitches;
        if (facilityList && Array.isArray(facilityList)) {
          const list = this.mapPitchesWithNormalizedImages(facilityList);
          this.pitches = list;
          this.filteredPitches = list;
        } else if (Array.isArray(result)) {
          const list = this.mapPitchesWithNormalizedImages(result);
          this.pitches = list;
          this.filteredPitches = list;
        } else {
          console.error('Unexpected response format:', result);
          this.pitches = [];
          this.filteredPitches = [];
        }
        
      } catch (error) {
        console.error('Error fetching pitches:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async filterPitches() {
      this.isLoading = true;
      try {
        const queryParams = new URLSearchParams();
        
        Object.entries(this.filters).forEach(([key, value]) => {
          if (value && key !== 'sort_by') {
            const paramName = key === 'search' ? 'search' : 
                            key === 'pitch_type' ? 'type_filter' :
                            key === 'tier' ? 'tier_filter' :
                            key === 'county' ? 'county_filter' :
                            key === 'zone' ? 'zone_filter' :
                            key === 'capacity' ? 'capacity_filter' :
                            key === 'price' ? 'price_filter' : key;
            
            queryParams.append(paramName, value);
          }
        });

        if (this.filters.sort_by) {
          if (this.filters.sort_by.includes('_desc')) {
            queryParams.append('sort_by', this.filters.sort_by.replace('_desc', ''));
            queryParams.append('sort_order', 'desc');
          } else {
            queryParams.append('sort_by', this.filters.sort_by);
            queryParams.append('sort_order', 'asc');
          }
        }

        const response = await fetch(buildApiUrl(`/api/facilities/all?${queryParams}`), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to filter sport facilities: ${response.status}`);
        }

        const result = await response.json();
        this.imageErrors = {};

        const facilityList = result.facilities || result.pitches;
        if (facilityList && Array.isArray(facilityList)) {
          this.filteredPitches = this.mapPitchesWithNormalizedImages(facilityList);
        } else if (Array.isArray(result)) {
          this.filteredPitches = this.mapPitchesWithNormalizedImages(result);
        } else {
          this.filteredPitches = [];
        }
        
      } catch (error) {
        console.error('Error filtering pitches:', error);
      } finally {
        this.isLoading = false;
      }
    },

    updateZoneOptions() {
      this.filters.zone = "";
      this.filterPitches();
    },

    clearFilters() {
      this.filters = {
        search: "",
        pitch_type: "",
        county: "",
        zone: "",
        tier: "",
        capacity: "",
        price: "",
        sort_by: "created_at"
      };
      this.filterPitches();
    },

    removeFilter(filterName) {
      this.filters[filterName] = "";
      this.filterPitches();
    },

    formatFilterValue(value) {
      return value.charAt(0).toUpperCase() + value.slice(1).replace('-', ' ');
    },

    getCapacityLabel(capacity) {
      const labels = {
        small: "Small (1-20)",
        medium: "Medium (21-50)",
        large: "Large (51-99)",
        extra_large: "Extra Large (100+)"
      };
      return labels[capacity] || capacity;
    },

    getPriceLabel(price) {
      const labels = {
        budget: "Budget (≤ Ksh 1,000)",
        midrange: "Mid-range (Ksh 1,001-3,000)",
        premium: "Premium (Ksh 3,001-6,000)",
        luxury: "Luxury (Ksh 6,000+)"
      };
      return labels[price] || price;
    },

    formatPrice(price) {
      return new Intl.NumberFormat('en-KE').format(price);
    },

    formatPitchType(type) {
      return formatFacilityType(type);
    },

    bookPitch(pitch) {
      this.$router.push(`/ipromiseafrica/facility/${pitch.id}/booking`);
    },

    viewPitchDetails(pitch) {
      this.$router.push(`/ipromiseafrica/facility/${pitch.id}/booking`);
    },

    hasPitchImage(pitch) {
      return (
        pitch.images &&
        pitch.images.length > 0 &&
        pitch.images[0] &&
        !this.imageErrors[pitch.id]
      );
    },

    onPitchImageError(pitchId) {
      this.imageErrors = { ...this.imageErrors, [pitchId]: true };
    },

    setupScrollListener() {
      window.addEventListener('scroll', this.handleScroll);
    },

    removeScrollListener() {
      window.removeEventListener('scroll', this.handleScroll);
    },

    handleScroll() {
      this.showBackToTop = window.scrollY > 500;
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    // Transition group hooks
    onBeforeEnter(el) {
      el.style.opacity = 0;
      el.style.transform = 'translateY(20px)';
    },

    onEnter(el, done) {
      const delay = el.dataset.index * 50;
      setTimeout(() => {
        el.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
        el.style.opacity = 1;
        el.style.transform = 'translateY(0)';
        done();
      }, delay);
    },

    onLeave(el, done) {
      el.style.transition = 'all 0.3s ease';
      el.style.opacity = 0;
      el.style.transform = 'translateY(20px)';
      setTimeout(done, 300);
    }
  }
};
</script>

<style scoped>
/* CSS Variables using your provided colors - Enhanced */
:root {
  --animation-duration: 0.3s;
  --transition-easing: cubic-bezier(0.4, 0, 0.2, 1);
  --primary-color: #3a86ff;
  --primary-hover: #2667cc;
  --secondary-color: #6c757d;
  --success-color: #4caf50;
  --warning-color: #ff9800;
  --error-color: #f44336;
  --info-color: #2196f3;
  
  /* Enhanced shadows for depth */
  --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
  --shadow-xl: 0 12px 36px rgba(0, 0, 0, 0.15);
  
  /* Border radius scale */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 16px;
  --radius-full: 9999px;
  
  /* Spacing scale */
  --space-xs: 4px;
  --space-sm: 8px;
  --space-md: 16px;
  --space-lg: 24px;
  --space-xl: 32px;
  --space-2xl: 48px;
}

.pitch-booking {
  background: var(--background-color1);
  width: 100%;
  min-height: 100vh;
  position: relative;
}

.edit-pitch-footer-host {
  margin: 0;
  padding: 0;
  width: 100%;
  flex-shrink: 0;
}

.edit-pitch-footer-host :deep(.thirdparty-footer) {
  margin-top: 0;
}

/* Header Section - Enhanced */
.header-section {
  position: relative;
  background: linear-gradient(135deg, var(--background-color1) 0%, rgba(58, 134, 255, 0.05) 100%);
  padding: 80px 20px 40px;
  text-align: center;
  margin-bottom: 0;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-hover));
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.header-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  animation: fadeInUp 0.6s var(--transition-easing);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  margin-bottom: 15px;
  color: var(--text-color1);
  background: linear-gradient(135deg, var(--text-color1) 0%, var(--primary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}

.header-subtitle {
  font-size: clamp(1rem, 2vw, 1.2rem);
  color: var(--text-color2);
  margin: 0 auto;
  max-width: 600px;
  line-height: 1.6;
  opacity: 0.9;
}

/* Fixed Filters Section - Enhanced */
.filters-section {
  background: var(--background-color1);
  padding: 20px 0;
  box-shadow: var(--shadow-md);
  border-bottom: 1px solid var(--border-color-hover1);
  transition: all var(--animation-duration) var(--transition-easing);
}

.filters-section.scrolled {
  box-shadow: var(--shadow-lg);
  padding: 15px 0;
}

.filters-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Main Search - Enhanced */
.main-search {
  position: relative;
  margin-bottom: 20px;
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  font-size: 1.1rem;
  transition: all var(--animation-duration) var(--transition-easing);
  z-index: 2;
}

.search-input {
  width: 100%;
  padding: 16px 60px 16px 50px;
  border: 2px solid var(--border-color-hover1);
  border-radius: var(--radius-lg);
  font-size: 1rem;
  background: var(--background-color1);
  color: var(--text-color1);
  transition: all var(--animation-duration) var(--transition-easing);
  font-weight: 500;
  box-shadow: var(--shadow-sm);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(58, 134, 255, 0.1);
  transform: translateY(-1px);
}

.search-input::placeholder {
  color: var(--text-color2);
  opacity: 0.7;
  font-weight: 400;
}

.clear-search-btn {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--background-color2);
  border: none;
  color: var(--text-color2);
  font-size: 0.9rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all var(--animation-duration) var(--transition-easing);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.clear-search-btn:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-50%) scale(1.1);
}

/* Filter Grid - Enhanced */
.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 15px;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-color1);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
}

.filter-group label i {
  color: var(--primary-color);
  font-size: 0.9rem;
  width: 16px;
  text-align: center;
}

.select-wrapper {
  position: relative;
  width: 100%;
}

.filter-select {
  width: 100%;
  padding: 12px 40px 12px 14px;
  border: 2px solid var(--border-color-hover1);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  background: var(--background-color1);
  color: var(--text-color1);
  transition: all var(--animation-duration) var(--transition-easing);
  cursor: pointer;
  appearance: none;
  font-weight: 500;
  box-shadow: var(--shadow-sm);
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(58, 134, 255, 0.1);
}

.filter-select:hover:not(:disabled) {
  border-color: var(--primary-color);
}

.filter-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--background-color2);
}

.select-arrow {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  pointer-events: none;
  font-size: 0.8rem;
  transition: transform var(--animation-duration) var(--transition-easing);
}

.filter-select:focus + .select-arrow {
  transform: translateY(-50%) rotate(180deg);
}

/* Clear Button - Enhanced */
.clear-btn {
  padding: 12px 20px;
  background: transparent;
  color: var(--text-color2);
  border: 2px solid var(--border-color-hover1);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--animation-duration) var(--transition-easing);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  height: 44px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 120px;
}

.clear-btn.has-filters {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
  color: white;
  border-color: transparent;
  box-shadow: var(--shadow-md);
}

.clear-btn:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.clear-btn.has-filters:hover {
  background: linear-gradient(135deg, var(--primary-hover), var(--primary-color));
  transform: translateY(-2px) scale(1.02);
  box-shadow: var(--shadow-lg);
}

.filter-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: white;
  color: var(--primary-color);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: var(--shadow-sm);
  border: 2px solid var(--primary-color);
}

/* Active Filters - Enhanced */
.slide-down-enter-active {
  animation: slideDownExpand 0.4s var(--transition-easing);
}

.slide-down-leave-active {
  animation: slideDownExpand 0.4s var(--transition-easing) reverse;
}

@keyframes slideDownExpand {
  from {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
    padding-top: 0;
    padding-bottom: 0;
    transform: translateY(-10px);
  }
  to {
    max-height: 200px;
    opacity: 1;
    margin-top: 20px;
    padding-top: 15px;
    padding-bottom: 15px;
    transform: translateY(0);
  }
}

.active-filters {
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.05), rgba(58, 134, 255, 0.02));
  border-radius: var(--radius-lg);
  border: 2px dashed var(--border-color-hover1);
  padding: 15px;
  margin-top: 20px;
  backdrop-filter: blur(5px);
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color-hover1);
}

.active-filters-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 8px;
}

.active-filters-label i {
  color: var(--primary-color);
  font-size: 0.9rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.clear-small-btn {
  padding: 6px 12px;
  background: transparent;
  color: var(--text-color2);
  border: 1px solid var(--border-color-hover1);
  border-radius: var(--radius-sm);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--animation-duration) var(--transition-easing);
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clear-small-btn:hover {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
  transform: translateY(-1px);
}

.active-filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-fade-enter-active,
.tag-fade-leave-active {
  transition: all 0.4s var(--transition-easing);
}

.tag-fade-enter-from,
.tag-fade-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(10px);
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, var(--background-color1), var(--background-color2));
  color: var(--text-color1);
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color-hover1);
  transition: all var(--animation-duration) var(--transition-easing);
  cursor: default;
}

.filter-tag:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.filter-tag i:first-child {
  font-size: 0.8rem;
  opacity: 0.9;
}

.remove-tag {
  margin-left: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  opacity: 0.7;
  transition: all var(--animation-duration) var(--transition-easing);
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.remove-tag:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

/* Results Summary - Enhanced */
.results-summary {
  max-width: 1400px;
  margin: 60px auto 30px;
  padding: 0 20px;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s var(--transition-easing);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.results-container {
  background: linear-gradient(135deg, var(--background-color2), rgba(58, 134, 255, 0.05));
  padding: 20px 30px;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border: 2px solid transparent;
  background-clip: padding-box;
  position: relative;
  overflow: hidden;
}

.results-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: var(--radius-lg);
  padding: 2px;
  background: linear-gradient(135deg, var(--primary-color), transparent);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.3;
}

.results-text {
  color: var(--text-color1);
  font-size: 1.1rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.results-count {
  font-size: 2rem;
  font-weight: 800;
  color: var(--primary-color);
  position: relative;
  padding-right: 10px;
}

.results-count::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 30px;
  background: var(--border-color-hover1);
}

.results-filtered {
  font-size: 0.9rem;
  color: var(--text-color2);
  font-weight: 500;
  padding: 4px 12px;
  background: var(--background-color1);
  border-radius: var(--radius-full);
  border: 1px solid var(--border-color-hover1);
}

/* Loading Section - Enhanced */
.loading-section {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(var(--background-color2-rgb, 255, 255, 255), 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(10px);
}

.loading-container {
  text-align: center;
  max-width: 400px;
  padding: 40px;
  background: var(--background-color1);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  border: 2px solid var(--border-color-hover1);
  animation: slideUp 0.4s var(--transition-easing);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.spinner {
  width: 60px;
  height: 60px;
  border: 3px solid transparent;
  border-top: 3px solid var(--primary-color);
  border-right: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 25px;
  position: relative;
}

.spinner::before {
  content: '';
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top: 3px solid rgba(58, 134, 255, 0.3);
  border-right: 3px solid rgba(58, 134, 255, 0.3);
  animation: spin 1.5s linear infinite reverse;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: var(--text-color1);
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
  animation: pulseText 1.5s ease-in-out infinite;
}

@keyframes pulseText {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* Sport facilities grid */
.staggered-fade-move {
  transition: transform 0.6s var(--transition-easing);
}

.pitch-grid {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
}

.pitch-card {
  background: var(--background-color2);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: all 0.4s var(--transition-easing);
  cursor: pointer;
  border: 2px solid transparent;
  position: relative;
  animation: cardAppear 0.5s var(--transition-easing) backwards;
}

@keyframes cardAppear {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.pitch-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: var(--shadow-xl);
  border-color: var(--primary-color);
  z-index: 10;
}

.pitch-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), transparent);
  opacity: 0;
  transition: opacity var(--animation-duration) var(--transition-easing);
}

.pitch-card:hover::before {
  opacity: 1;
}

/* Sport facility image */
.pitch-image {
  position: relative;
  height: 220px;
  overflow: hidden;
}

.pitch-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s var(--transition-easing);
  filter: brightness(0.95);
}

.pitch-card:hover .pitch-image img {
  transform: scale(1.1);
  filter: brightness(1);
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--background-color1), var(--background-color2));
  color: var(--text-color2);
  font-size: 3rem;
  opacity: 0.7;
}

.image-overlay {
  position: absolute;
  top: 15px;
  left: 15px;
  right: 15px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.price-tag, .tier-tag {
  padding: 8px 16px;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 700;
  color: white;
  display: inline-block;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-sm);
  transition: all var(--animation-duration) var(--transition-easing);
}

.price-tag {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
}

.pitch-card:hover .price-tag {
  transform: translateY(-2px) scale(1.05);
}

.tier-tag {
  background: rgba(0, 0, 0, 0.7);
  color: white;
}

.tier-tag.premium {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #000;
}

.tier-tag.standard {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
}

.tier-tag.basic {
  background: linear-gradient(135deg, #2196F3, #1976D2);
  color: white;
}

/* Sport facility card content */
.pitch-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.pitch-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.pitch-header h3 {
  margin: 0;
  font-size: 1.3rem;
  color: var(--text-color1);
  font-weight: 700;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pitch-location {
  margin: 0;
}

.pitch-location p {
  margin: 6px 0;
  color: var(--text-color1);
  font-size: 0.95rem;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  line-height: 1.4;
}

.pitch-location i {
  color: var(--primary-color);
  font-size: 0.9rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.location-details {
  color: var(--text-color2) !important;
  font-size: 0.9rem !important;
  opacity: 0.9;
}

.pitch-features {
  display: flex;
  gap: 12px;
  margin: 0;
  flex-wrap: wrap;
}

.feature {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-color1);
  font-size: 0.9rem;
  padding: 8px 14px;
  background: var(--background-color1);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color-hover1);
  transition: all var(--animation-duration) var(--transition-easing);
  font-weight: 500;
}

.pitch-card:hover .feature {
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.1), transparent);
  border-color: rgba(58, 134, 255, 0.3);
}

.feature i {
  color: var(--primary-color);
  font-size: 0.9rem;
}

.pitch-actions {
  display: flex;
  gap: 12px;
  margin-top: 10px;
}

.book-btn, .details-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: var(--radius-md);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s var(--transition-easing);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.book-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
  color: var(--text-color1);
  box-shadow: var(--shadow-sm);
}

.book-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  background: linear-gradient(135deg, var(--primary-hover), var(--primary-color));
  color: var(--text-color1);
}

.details-btn {
  background: transparent;
  color: var(--text-color1);
  border: 2px solid var(--border-color-hover1);
  font-weight: 600;
}

.details-btn:hover {
  background: var(--background-color1);
  border-color: var(--primary-color);
  color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
}

/* No Results - Enhanced */
.no-results {
  grid-column: 1 / -1;
  text-align: center;
  padding: 80px 20px;
  animation: fadeIn 0.6s var(--transition-easing);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.no-results-content {
  max-width: 500px;
  margin: 0 auto;
}

.no-results-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, var(--background-color2), var(--background-color1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  font-size: 3rem;
  color: var(--primary-color);
  border: 2px dashed var(--border-color-hover1);
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.no-results h3 {
  margin: 0 0 15px 0;
  color: var(--text-color1);
  font-size: 1.8rem;
  font-weight: 700;
}

.no-results p {
  color: var(--text-color2);
  font-size: 1.1rem;
  margin-bottom: 30px;
  line-height: 1.6;
}

.clear-all-btn {
  padding: 14px 28px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s var(--transition-easing);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: var(--shadow-md);
}

.clear-all-btn:hover {
  background: linear-gradient(135deg, var(--primary-hover), var(--primary-color));
  transform: translateY(-3px) scale(1.05);
  box-shadow: var(--shadow-lg);
}

/* Back to Top Button - Enhanced */
.back-to-top {
  position: fixed;
  bottom: 40px;
  right: 40px;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-lg);
  transition: all 0.4s var(--transition-easing);
  z-index: 99;
  opacity: 0;
  transform: translateY(20px);
}

.back-to-top.show {
  opacity: 1;
  transform: translateY(0);
}

.back-to-top:hover {
  background: linear-gradient(135deg, var(--primary-hover), var(--primary-color));
  transform: translateY(-5px) scale(1.1);
  box-shadow: var(--shadow-xl);
}

.back-to-top:active {
  transform: translateY(-2px) scale(0.95);
}

/* Page Image — full viewport width banner */
.page-image {
  width: 100vw;
  max-width: 100vw;
  margin-inline: calc(50% - 50vw);
  margin-top: clamp(2rem, 5vw, 3.75rem);
  margin-bottom: 0;
  padding: 0;
  line-height: 0;
  overflow: hidden;
}

.page-image img {
  width: 100%;
  height: clamp(160px, 30vw, 420px);
  min-height: 140px;
  max-height: min(50vh, 480px);
  object-fit: cover;
  object-position: center center;
  display: block;
  border-radius: 0;
  box-shadow: none;
  filter: none;
}

/* Custom scrollbar - Enhanced */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

::-webkit-scrollbar-track {
  background: var(--background-color1);
  border-radius: var(--radius-full);
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover));
  border-radius: var(--radius-full);
  border: 2px solid var(--background-color1);
  transition: all var(--animation-duration) var(--transition-easing);
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, var(--primary-hover), var(--primary-color));
  transform: scale(1.1);
}

::-webkit-scrollbar-corner {
  background: var(--background-color1);
}

/* Focus styles for accessibility */
*:focus {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

*:focus:not(:focus-visible) {
  outline: none;
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    animation-delay: 0ms !important;
    transition-delay: 0ms !important;
  }
  
  .pitch-card,
  .back-to-top,
  .clear-all-btn,
  .book-btn,
  .details-btn,
  .filter-tag {
    transition: none !important;
  }
  
  .pitch-card:hover {
    transform: none !important;
  }
}

/* Responsive Design - Enhanced */
@media (max-width: 1200px) {
  .pitch-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 25px;
  }
  
  .filters-container {
    padding: 0 30px;
  }
}

@media (max-width: 992px) {
  .header-title {
    font-size: 2.5rem;
  }
  
  .filter-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .pitch-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
  }
  
  .back-to-top {
    bottom: 30px;
    right: 30px;
    width: 45px;
    height: 45px;
  }
}

@media (max-width: 768px) {
  .header-section {
    padding: 60px 20px 30px;
  }
  
  .filters-section {
    position: static;
    padding: 15px 0;
  }
  
  .results-summary {
    margin: 40px auto 20px;
  }
  
  .filter-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .filters-container {
    padding: 0 20px;
  }
  
  .pitch-grid {
    padding: 15px;
    grid-template-columns: 1fr;
    gap: 25px;
  }
  
  .pitch-actions {
    flex-direction: column;
  }
  
  .active-filter-tags {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-tag {
    width: 100%;
    justify-content: space-between;
  }
  
  .back-to-top {
    bottom: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }

  .page-image img {
    height: clamp(130px, 26vw, 280px);
    min-height: 120px;
  }
}

@media (max-width: 480px) {
  .header-title {
    font-size: 2rem;
  }
  
  .header-subtitle {
    font-size: 1rem;
  }
  
  .search-input {
    padding: 14px 55px 14px 45px;
    font-size: 0.95rem;
  }
  
  .clear-btn span {
    display: none;
  }
  
  .clear-btn::after {
    content: 'Clear Filters';
  }
  
  .results-container {
    padding: 15px 20px;
  }
  
  .results-count {
    font-size: 1.8rem;
  }
  
  .no-results-icon {
    width: 80px;
    height: 80px;
    font-size: 2.5rem;
  }
  
  .no-results h3 {
    font-size: 1.5rem;
  }

  .page-image {
    margin-top: clamp(1.5rem, 4vw, 2rem);
  }

  .page-image img {
    height: clamp(120px, 32vw, 220px);
    min-height: 110px;
    max-height: 38vh;
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .filters-section {
    background-color: rgba(var(--background-color2-rgb, 30, 30, 30), 0.95);
  }
  
  .search-input,
  .filter-select {
    background: rgba(255, 255, 255, 0.05);
  }
  
  .filter-tag {
    background: rgba(255, 255, 255, 0.1);
  }
  
  .active-filters {
    background: rgba(58, 134, 255, 0.05);
  }
  
  .results-container {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(58, 134, 255, 0.1));
  }
}

/* Print styles */
@media print {
  .filters-section,
  .back-to-top,
  .pitch-actions,
  .page-image {
    display: none !important;
  }
  
  .pitch-grid {
    display: block !important;
  }
  
  .pitch-card {
    break-inside: avoid;
    box-shadow: none !important;
    border: 1px solid #ccc !important;
    margin-bottom: 20px;
  }
}

/* Performance optimizations */
@media (prefers-contrast: high) {
  .header-title {
    background: none;
    -webkit-text-fill-color: var(--text-color1);
  }
  
  .pitch-card:hover {
    outline: 2px solid var(--primary-color);
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .pitch-card:hover {
    transform: none;
  }
  
  .filter-select {
    min-height: 44px; /* Better touch target */
  }
  
  .book-btn,
  .details-btn,
  .clear-btn {
    min-height: 44px;
  }
}
</style>